import { ScreeningQuestion } from '@/types';
import { ScreeningQuestionType } from '@/enums';

/**
 * NUMERIC screening questions store their bounds in `numericResponse`, not
 * `responses`. Strip `responses` for those before sending to the backend —
 * moved out of ProjectCreate.tsx so it can be reused by both the full-save
 * and partial-save (TELV1-490) code paths without duplicating the mapping.
 */
export const sanitizeScreeningQuestionsForPayload = (
  questions: ScreeningQuestion[],
): ScreeningQuestion[] =>
  questions.map((q) => {
    const base = q.type === ScreeningQuestionType.NUMERIC ? { ...q, responses: undefined } : q;
    // Media rides the project save only for label/order edits (files are
    // uploaded through the dedicated media endpoint). Strip the read-time
    // signed `url` — it's not part of the stored shape, and the backend DTO
    // validates screening media strictly (@ValidateNested). `file_id` is
    // stripped for symmetry with the respondent-serving shape (worker adds
    // it alongside url); it never appears in the authoring flow today.
    // TELV1-520.
    if (!Array.isArray(base.media)) return base;
    return {
      ...base,
      media: base.media.map((item) => {
        const {
          url: _url,
          file_id: _fileId,
          ...mediaRest
        } = item as typeof item & {
          file_id?: string;
        };
        return mediaRest;
      }) as typeof base.media,
    };
  });

/**
 * Drops screener answer rows that carry no intent at all: an empty value
 * AND no rule selected. A row with either a value or a rule set is a user
 * in the middle of filling it in — that's a real validation error, not
 * scratch state, so it is left alone and surfaces the normal "please
 * select one of the rules" / "required" error instead of being silently
 * deleted (TELV1-490 architect review, Point 4).
 */
export const pruneNoIntentScreeningAnswers = (
  questions: ScreeningQuestion[] | undefined,
): ScreeningQuestion[] | undefined => {
  if (!questions) return questions;

  let didPrune = false;
  const pruned = questions.map((question) => {
    if (!Array.isArray(question.responses)) return question;

    const keptResponses = question.responses.filter(
      (response) => !(response.value?.trim() === '' && !response.rule),
    );

    if (keptResponses.length === question.responses.length) return question;
    didPrune = true;
    return { ...question, responses: keptResponses };
  });

  return didPrune ? pruned : questions;
};
